/* eslint-disable import/no-unresolved,react/prop-types */
import React, { ComponentType, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material';
import { Button, Typography } from '@mui/material';
import { CustomTooltip } from './components/CustomTooltip';
import { WithActionProps } from './WithAction.props';

const PREFIX = 'withAction';

const classes = {
  root: `${PREFIX}-root`,
  margin: `${PREFIX}-margin`,
  marginBetweenActions: `${PREFIX}-marginBetweenActions`,
  iconContent: `${PREFIX}-iconContent`,
  iconContainer: `${PREFIX}-iconContainer`,
  center: `${PREFIX}-center`,
  right: `${PREFIX}-right`,
  icon: `${PREFIX}-icon`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    position: 'relative',
  },

  [`& .${classes.margin}`]: {
    marginTop: theme.spacing(3),
  },

  [`& .${classes.marginBetweenActions}`]: {
    marginTop: theme.spacing(5),
  },

  [`& .${classes.iconContent}`]: {
    width: '100%',
    minHeight: '100%',
    display: 'flex',
    alignItems: 'center',
  },

  [`& .${classes.iconContainer}`]: {
    position: 'absolute',
    cursor: 'pointer',
    padding: 0,
  },

  [`& .${classes.center}`]: {
    top: 16,
    left: 80,
  },

  [`& .${classes.right}`]: {
    top: 8,
    right: 0,
  },

  [`& .${classes.icon}`]: {},
}));

// eslint-disable-next-line react/display-name
const withAction = (WrappedComponent: ComponentType<any>) =>
  function (props: WithActionProps) {
    const { action, extraActions, key = null, value = null } = props.cell;
    const [isIconVisible, setIsIconVisible] = useState<boolean>(false);
    const [divRect, setDivRect] = useState<DOMRect | null>(null);
    const divRef = useRef<HTMLDivElement>(null);

    const Icon = useMemo(() => {
      if (action) {
        return () =>
          React.cloneElement(action.icon, {
            classes: {
              root: classes.icon,
            },
          });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, classes.icon]);

    useEffect(() => {
      if (divRef.current) {
        setDivRect(divRef.current.getBoundingClientRect());
      }

      return () => setDivRect(null);
    }, [isIconVisible]);

    const tooltipPosition = useMemo(() => {
      if (divRect) {
        if (divRect.right + divRect.width > window.innerWidth) {
          return 'top-end';
        }
        return 'top';
      }
    }, [divRect]);

    const tooltipContent = useMemo(() => {
      if (action) {
        return (
          <>
            <Typography align="center">{action.tooltip.title}</Typography>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              className={classes.margin}
              onClick={action.callback}>
              {action.tooltip.buttonText || 'Calculate'}
            </Button>

            {!!extraActions
              && extraActions.map(extraAction => (
                <Fragment key={key}>
                  <Typography className={classes.marginBetweenActions} align="center">
                    {extraAction.title}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    className={classes.margin}
                    onClick={extraAction.callback}>
                    {extraAction.buttonText}
                  </Button>
                </Fragment>
              ))}
          </>
        );
      }
      return <></>;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, classes.margin, classes.marginBetweenActions, extraActions, key]);

    if (!action) {
      return <WrappedComponent {...props} />;
    }

    return (
      <Root
        className={classes.root}
        onMouseEnter={() => setIsIconVisible(true)}
        onMouseLeave={() => setIsIconVisible(false)}
        ref={divRef}>
        <WrappedComponent {...props} />
        <CustomTooltip
          arrow
          // interactive
          enterDelay={action.tooltip.enterDelay}
          enterNextDelay={action.tooltip.enterNextDelay}
          placement={tooltipPosition}
          title={tooltipContent}>
          <div className={`${classes.iconContainer} ${action.position}`}>
            <div className={classes.iconContent}>{isIconVisible && Icon && <Icon />}</div>
          </div>
        </CustomTooltip>
      </Root>
    );
  };

export default withAction;
